import {
  computed,
  onBeforeUnmount,
  onMounted,
  readonly,
  ref,
  type ComputedRef,
  type CSSProperties,
  type DeepReadonly,
  type Ref,
} from 'vue';

export interface IVisualViewportBounds {
  height: number;
  offsetLeft: number;
  offsetTop: number;
  width: number;
}

interface IUseVisualViewportBounds {
  bounds: DeepReadonly<Ref<IVisualViewportBounds>>;
  fixedViewportStyle: ComputedRef<CSSProperties>;
}

type TViewportWindow = Pick<
  Window,
  'addEventListener' | 'innerHeight' | 'innerWidth' | 'removeEventListener' | 'visualViewport'
>;

const getBrowserWindow = (): TViewportWindow | undefined => (typeof window === 'undefined' ? undefined : window);

export const resolveVisualViewportBounds = (viewportWindow?: TViewportWindow): IVisualViewportBounds => {
  if (!viewportWindow) {
    return { height: 0, offsetLeft: 0, offsetTop: 0, width: 0 };
  }

  const viewport = viewportWindow.visualViewport;
  return {
    height: viewport?.height ?? viewportWindow.innerHeight,
    offsetLeft: viewport?.offsetLeft ?? 0,
    offsetTop: viewport?.offsetTop ?? 0,
    width: viewport?.width ?? viewportWindow.innerWidth,
  };
};

export const getFixedVisualViewportStyle = (bounds: IVisualViewportBounds): CSSProperties => ({
  height: `${bounds.height}px`,
  left: `${bounds.offsetLeft}px`,
  top: `${bounds.offsetTop}px`,
  width: `${bounds.width}px`,
});

export const subscribeToVisualViewportChanges = (
  viewportWindow: TViewportWindow | undefined,
  listener: () => void,
): (() => void) => {
  if (!viewportWindow) return () => undefined;

  viewportWindow.addEventListener('resize', listener);
  viewportWindow.visualViewport?.addEventListener('resize', listener);
  viewportWindow.visualViewport?.addEventListener('scroll', listener);

  return () => {
    viewportWindow.removeEventListener('resize', listener);
    viewportWindow.visualViewport?.removeEventListener('resize', listener);
    viewportWindow.visualViewport?.removeEventListener('scroll', listener);
  };
};

/** Keeps fixed mobile surfaces inside the visible area while the software keyboard is open. */
export const useVisualViewportBounds = (): IUseVisualViewportBounds => {
  const viewportWindow = getBrowserWindow();
  const bounds = ref<IVisualViewportBounds>(resolveVisualViewportBounds(viewportWindow));
  let unsubscribe = (): void => undefined;

  const updateBounds = (): void => {
    bounds.value = resolveVisualViewportBounds(viewportWindow);
  };

  onMounted(() => {
    updateBounds();
    unsubscribe = subscribeToVisualViewportChanges(viewportWindow, updateBounds);
  });

  onBeforeUnmount(() => unsubscribe());

  return {
    bounds: readonly(bounds),
    fixedViewportStyle: computed(() => getFixedVisualViewportStyle(bounds.value)),
  };
};
